import { useEffect, useRef, useState } from 'react';
import type { RegistryEntry } from '@unified-governance/shared';
import { loadRemoteMount } from './federation';
import { ErrorBoundary } from './ErrorBoundary';
import type { ShellAuth } from './auth/ShellAuthContext';
import type { ShellTelemetry } from './telemetry/shellTelemetry';

interface RemoteOutletProps {
  entry: RegistryEntry;
  auth: ShellAuth;
  telemetry: ShellTelemetry;
}

export const RemoteOutlet = ({ entry, auth, telemetry }: RemoteOutletProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const authRef = useRef(auth);
  const telemetryRef = useRef(telemetry);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    authRef.current = auth;
  }, [auth]);

  useEffect(() => {
    telemetryRef.current = telemetry;
  }, [telemetry]);

  useEffect(() => {
    let active = true;
    let cleanup: (() => void) | undefined;
    const element = containerRef.current;
    if (!element) {
      return;
    }
    // React remotes can return from mount before their first DOM commit.
    const observer = new MutationObserver(() => {
      if (active && element.hasChildNodes()) {
        observer.disconnect();
        setLoading(false);
      }
    });
    setLoading(true);
    setFailed(false);
    const start = performance.now();
    telemetryRef.current.event('remote.load.start', {
      remoteName: entry.name,
      basePath: entry.basePath,
      url: entry.url,
    });

    loadRemoteMount(entry.name)
      .then((mount) => {
        if (!mount) {
          throw new Error(`Remote ${entry.name} did not expose a mount function`);
        }
        if (!active) {
          return;
        }
        telemetryRef.current.event('remote.load.success', {
          remoteName: entry.name,
          basePath: entry.basePath,
          durationMs: Math.round(performance.now() - start),
        });
        observer.observe(element, { childList: true });
        cleanup = mount(element, {
          base: `/${entry.basePath}`,
          hosted: true,
          auth: {
            getAccessToken: (scope: string) => authRef.current.getAccessToken(scope),
            roles: authRef.current.roles,
            user: authRef.current.user,
          },
          telemetry: telemetryRef.current.forRemote(entry.name, entry.basePath),
        });
        telemetryRef.current.event('remote.mount', {
          remoteName: entry.name,
          basePath: entry.basePath,
        });
      })
      .catch((error: unknown) => {
        observer.disconnect();
        telemetryRef.current.event('remote.load.error', {
          remoteName: entry.name,
          basePath: entry.basePath,
          error: error instanceof Error ? error.message : String(error),
        });
        if (active) {
          setLoading(false);
          setFailed(true);
        }
      });

    return () => {
      active = false;
      observer.disconnect();
      if (cleanup) {
        try {
          cleanup();
          telemetryRef.current.event('remote.unmount', {
            remoteName: entry.name,
            basePath: entry.basePath,
          });
        } catch (error: unknown) {
          telemetryRef.current.event('remote.unmount.error', {
            remoteName: entry.name,
            basePath: entry.basePath,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    };
  }, [entry.name, entry.basePath, entry.url]);

  if (failed) {
    return <div className="shell-status">Unable to load {entry.name}.</div>;
  }

  return (
    <ErrorBoundary
      onError={(error) => {
        telemetryRef.current.event('remote.error', {
          remoteName: entry.name,
          basePath: entry.basePath,
          error: error.message,
        });
        setLoading(false);
      }}
      fallback={<div className="shell-status">The {entry.name} module is unavailable.</div>}
    >
      <>
        {loading && <div className="shell-status">Loading {entry.name}...</div>}
        <div
          ref={containerRef}
          className="shell-remote-root"
          data-remote-name={entry.name}
          data-remote-basepath={entry.basePath}
        />
      </>
    </ErrorBoundary>
  );
};
